import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listDeployments,
  getDeployment,
  createDeployment,
  getDeploymentLogs,
  cancelDeployment,
  rollbackDeployment,
  retryDeployment,
  getDeploymentStats,
  getDeploymentOverview,
  getEnvironmentRuns,
  updateEnvironmentTriggers,
} from '../api/endpoints/deployments.ts';
import type { CreateDeploymentParams, DeploymentLog, DeploymentStatus, UpdateTriggersParams } from '../api/types.ts';
import { STALE_TIMES, GC_TIMES } from '../lib/constants.ts';
import { useAuthStore } from '../stores/auth-store.ts';
import { createAuthenticatedWebSocket } from '../lib/websocket.ts';

export const deploymentQueryKeys = {
  all: ['deployments'] as const,
  list: (params?: { projectId?: string; serverId?: string; environmentId?: string; environmentName?: string; status?: string }) =>
    ['deployments', 'list', params] as const,
  detail: (deploymentId: string) => ['deployments', 'detail', deploymentId] as const,
  logs: (deploymentId: string) => ['deployments', 'logs', deploymentId] as const,
  stats: (projectId?: string) => ['deployments', 'stats', projectId] as const,
  overview: () => ['deployments', 'overview'] as const,
  envRuns: (envId: string, params?: { page?: number; pageSize?: number }) => ['deployments', 'envRuns', envId, params] as const,
};

const TERMINAL_DEPLOYMENT_STATUSES: DeploymentStatus[] = [
  'success',
  'failed',
  'build_failed',
  'partial_failed',
  'cancelled',
  'rolled_back',
];

export function deploymentLogsRefetchInterval(
  status?: DeploymentStatus,
  live = false,
): number | false {
  const isTerminal = !!status && TERMINAL_DEPLOYMENT_STATUSES.includes(status);
  if (isTerminal || live) return false;
  return 5000;
}

/** Join deployment log room; shared by DeploymentLogs + useDeploymentLogs polling gate. */
export function useDeploymentLogLive(deploymentId: string, enabled: boolean) {
  const accessToken = useAuthStore((s) => s.accessToken);
  const [live, setLive] = useState(false);
  const [wsLogs, setWsLogs] = useState<DeploymentLog[]>([]);
  const [wsError, setWsError] = useState(false);

  useEffect(() => {
    if (!enabled || !accessToken || !deploymentId) {
      setLive(false);
      return;
    }

    let ws: WebSocket;
    try {
      ws = createAuthenticatedWebSocket(accessToken);
    } catch {
      setWsError(true);
      setLive(false);
      return;
    }

    setWsLogs([]);
    setWsError(false);

    ws.onopen = () => {
      setLive(true);
      setWsError(false);
      ws.send(JSON.stringify({ type: 'join', room: `deployment:${deploymentId}` }));
    };

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        const logEvent = msg.event || msg.type;
        const logData = msg.data;
        if (logEvent === 'deployment:log' && logData) {
          setWsLogs((prev) => [...prev, logData as DeploymentLog]);
        }
      } catch {
        // Ignore malformed messages
      }
    };

    ws.onerror = () => {
      setWsError(true);
      setLive(false);
    };

    ws.onclose = () => {
      setLive(false);
    };

    return () => {
      if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
        ws.close();
      }
    };
  }, [deploymentId, enabled, accessToken]);

  return { live, wsLogs, wsError };
}

export function useDeployments(params?: {
  page?: number;
  pageSize?: number;
  projectId?: string;
  serverId?: string;
  environmentId?: string;
  environmentName?: string;
  status?: string;
}) {
  return useQuery({
    queryKey: deploymentQueryKeys.list(params),
    queryFn: () => listDeployments(params),
    staleTime: STALE_TIMES.deployments,
    gcTime: GC_TIMES.deployments,
  });
}

export function useDeployment(deploymentId: string) {
  return useQuery({
    queryKey: deploymentQueryKeys.detail(deploymentId),
    queryFn: () => getDeployment(deploymentId),
    staleTime: STALE_TIMES.deployments,
    gcTime: GC_TIMES.deployments,
    enabled: !!deploymentId,
  });
}

export function useDeploymentLogs(
  deploymentId: string,
  status?: DeploymentStatus,
  options?: { live?: boolean },
) {
  const live = options?.live === true;
  return useQuery({
    queryKey: deploymentQueryKeys.logs(deploymentId),
    queryFn: () => getDeploymentLogs(deploymentId),
    staleTime: STALE_TIMES.deployments,
    gcTime: GC_TIMES.deployments,
    enabled: !!deploymentId,
    refetchInterval: deploymentLogsRefetchInterval(status, live),
    refetchIntervalInBackground: false,
  });
}

export function useDeploymentStats(projectId?: string) {
  return useQuery({
    queryKey: deploymentQueryKeys.stats(projectId),
    queryFn: () => getDeploymentStats(projectId ? { projectId } : undefined),
    staleTime: STALE_TIMES.deployments,
    gcTime: GC_TIMES.deployments,
  });
}

export function useCreateDeployment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: CreateDeploymentParams) => createDeployment(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: deploymentQueryKeys.all });
    },
  });
}

export function useCancelDeployment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (deploymentId: string) => cancelDeployment(deploymentId),
    onSuccess: (_data, deploymentId) => {
      queryClient.invalidateQueries({ queryKey: deploymentQueryKeys.detail(deploymentId) });
      queryClient.invalidateQueries({ queryKey: deploymentQueryKeys.all });
    },
  });
}

export function useRollbackDeployment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (deploymentId: string) => rollbackDeployment(deploymentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: deploymentQueryKeys.all });
    },
  });
}

export function useRetryDeployment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (deploymentId: string) => retryDeployment(deploymentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: deploymentQueryKeys.all });
    },
  });
}

export function useDeploymentOverview() {
  return useQuery({
    queryKey: deploymentQueryKeys.overview(),
    queryFn: getDeploymentOverview,
    staleTime: STALE_TIMES.deployments,
    gcTime: GC_TIMES.deployments,
  });
}

export function useEnvironmentRuns(envId: string, params?: { page?: number; pageSize?: number }) {
  return useQuery({
    queryKey: deploymentQueryKeys.envRuns(envId, params),
    queryFn: () => getEnvironmentRuns(envId, params),
    staleTime: STALE_TIMES.deployments,
    gcTime: GC_TIMES.deployments,
    enabled: !!envId,
  });
}

export function useUpdateTriggers() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ envId, params }: { envId: string; params: UpdateTriggersParams }) =>
      updateEnvironmentTriggers(envId, params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: deploymentQueryKeys.overview() });
    },
  });
}
